import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import Hey from "@/lib/hey-utils";
import { FlutterUtils, useFlutterBridge } from "@/hooks/useFlutterBridge";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { AuthHeader } from "@/components/auth/AuthHeader";
import { MethodTabs } from "@/components/auth/MethodTabs";
import { RegisterForm } from "@/components/auth/RegisterForm";
import { PasswordLogin } from "@/components/auth/PasswordLogin";
import { OtpLogin } from "@/components/auth/OtpLogin";
import { isValidEmail } from "@/utils/validation";
import { Badge } from "@/components/ui/badge";
import { useDevicePlatform } from "@/utils/device";
declare global {
  interface Window {
    flutter?: unknown;
  }
}
const Auth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emailsMatch, setEmailsMatch] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [showConfirmEmailAlert, setShowConfirmEmailAlert] = useState(false);
  const [authMethod, setAuthMethod] = useState<"password" | "otp" | "register">("otp");
  const [otpStep, setOtpStep] = useState<"email" | "code">("email");
  const [signUpStep, setSignUpStep] = useState<"start" | "otp" | "done">("start");
  const [otpCode, setOtpCode] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isFlutterWebappDetected, setIsFlutterWebapp] = useState(false);
  const [hasAdtdTripPWaiting, setHasAdtdTripPWaiting] = useState(false);
  const [language, setLanguage] = useState("en");
  const isFlutterWebapp = FlutterUtils.isFlutterWebapp();
  const navigate = useNavigate();
  const {
    t
  } = useTranslation();
  useFlutterBridge(null);
  useEffect(() => {
    try {
      let atdtData = localStorage.getItem("atdtData");
      if (atdtData) {
        atdtData = JSON.parse(atdtData);
        setHasAdtdTripPWaiting(true);
      }
    } catch (error) {
      console.error('Error retrieving atdtData from localStorage:', error);
    }
    const isMobileApp = typeof window !== 'undefined' && window.flutter !== undefined;
    if (isMobileApp) {
      const urlParams = new URLSearchParams(window.location.search);
      const flutterLanguage = urlParams.get('lang');
      if (flutterLanguage && i18n.languages.includes(flutterLanguage)) {
        console.debug(`🌐 Mobile mode: Language set from Flutter parameter: ${flutterLanguage}`);
        i18n.changeLanguage(flutterLanguage);
        setLanguage(flutterLanguage);
      } else {
        console.debug('🌐 Mobile mode: No valid language parameter from Flutter, using default language');
      }
    } else {
      const browserLanguage = navigator.language.split('-')[0];
      const supportedLanguage = i18n.languages.includes(browserLanguage) ? browserLanguage : 'en';
      console.debug(`🌐 Desktop mode: Detected browser language: ${browserLanguage}, using: ${supportedLanguage}`);
      i18n.changeLanguage(supportedLanguage);
      setLanguage(supportedLanguage);
    }
  }, []);
  async function checkIsLoggedIn() {
    try {
      if (await Hey.HeyUtils.getInstance().checkUserLogin()) {
        setIsLoggedIn(true);
      }
    } catch (error) {
      console.error('Error checking user login:', error);
    }
  }
  function getOrigin() {
    let origin = window.location.origin;
    origin = origin.replace("http://", "");
    origin = origin.replace("https://", "");
    origin = origin.split("/")[0];
    origin = origin.split(":")[0];
    return origin;
  }
  const { userAgent, platform } = useDevicePlatform();

   useEffect(() => {
    window.dispatchEvent(new Event("useragentchange"));
   },[]);


  useEffect(() => {
    console.info("User Agent:", userAgent);
    console.info("Device Platform:", platform);
  }, [userAgent, platform]);
  useEffect(() => {
    if (authMethod === "register") {
      setEmailsMatch(email === confirmEmail && email !== "");
      setPasswordsMatch(password === confirmPassword && password !== "");
    }
    checkIsLoggedIn();
  }, [email, confirmEmail, password, confirmPassword, authMethod]);
  useEffect(() => {
    if (language) {
      console.debug(`🌐 Language changed by user selection to: ${language}`);
      i18n.changeLanguage(language);
    }
  }, [language]);
  const resetForm = () => {
    setEmail("");
    setConfirmEmail("");
    setPassword("");
    setConfirmPassword("");
    setOtpCode("");
    setShowConfirmEmailAlert(false);
    setOtpStep("email");
  };
  if (isLoggedIn) {
    console.debug('User is already logged in, redirecting to /');
    navigate("/");
    return null;
  }
  const flutterCheckInterval = setInterval(() => {
    if (FlutterUtils.isFlutterWebapp()) {
      setIsFlutterWebapp(true);
      FlutterUtils.setTitle(t("auth.welcomeBack"));
      clearInterval(flutterCheckInterval);
      console.debug("Flutter webapp detected");
    }
  }, 150);
  if (Hey.HeyUtils.isOriginUnknown()) {
    return <div className="min-h-screen bg-gradient-to-b from-travel-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>{t("errors.configurationError")}</AlertTitle>
              <AlertDescription>
                {t("errors.websiteNotConfigured")} ({getOrigin()})
              </AlertDescription>
            </Alert>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen flex flex-col bg-gradient-to-b from-travel-50 to-white">
      
      
      <div className="flex-1 flex flex-col md:flex-row">
        <div className="flex-1 flex items-center justify-center px-4 md:px-8 lg:px-16 md:w-1/2 pt-10">
        
          <div className="w-full max-w-md space-y-8">
            
            <div className="flex items-center justify-center gap-6 mb-6">
              <img src={Hey.HeyUtils.getInstance().getConfig().clientLogoUrl} alt="Logo" className="w-16 h-16" />
              <span className="font-semibold text-2xl">{Hey.HeyUtils.getInstance().getConfig().clientDisplayName}</span>
            </div>

            <hr/>

            <h1 className="font-semibold mt-6 mb-8 text-2xl px-10 text-center">
              {t("auth.loginAppDescription")}
            </h1>
            <p className="text-gray-600 mb-8 text-left text-xl px-3" style={{
            textAlign: "justify"
          }}>
              {t("auth.loginDescription")}
            </p>

            <AuthHeader authMethod={authMethod} />

            {hasAdtdTripPWaiting && <Alert className="mb-4" style={{
            backgroundColor: "#ecfdf5",
            color: "#047857",
            border: "1px solid #10b981"
          }}>
              <div className="flex flex-col">
                <AlertTitle>{t("auth.adtdTripPWaiting")}</AlertTitle>
                <AlertDescription>
                  {t("auth.adtdTripPWaitingDescription")}
                </AlertDescription>
              </div>
            </Alert>}

            <MethodTabs authMethod={authMethod} setAuthMethod={setAuthMethod} resetForm={resetForm} />

            {showConfirmEmailAlert && <Alert className="mb-4">
              <AlertTitle>{t("auth.emailConfirmationRequired")}</AlertTitle>
              <AlertDescription>
                {t("auth.checkEmailForConfirmation")}
              </AlertDescription>
            </Alert>}

            {authMethod === "register" && <RegisterForm isLoading={isLoading} setIsLoading={setIsLoading} firstName={firstName} setFirstName={setFirstName} lastName={lastName} setLastName={setLastName} email={email} setEmail={setEmail} confirmEmail={confirmEmail} setConfirmEmail={setConfirmEmail} password={password} setPassword={setPassword} confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword} language={language} setLanguage={setLanguage} otpCode={otpCode} setOtpCode={setOtpCode} signUpStep={signUpStep} setSignUpStep={setSignUpStep} emailsMatch={emailsMatch} passwordsMatch={passwordsMatch} navigate={navigate} isValidEmail={isValidEmail} />}

            {authMethod === "password" && <PasswordLogin isLoading={isLoading} setIsLoading={setIsLoading} email={email} setEmail={setEmail} password={password} setPassword={setPassword} navigate={navigate} isValidEmail={isValidEmail} setAuthMethod={setAuthMethod} />}

            {authMethod === "otp" && <OtpLogin isLoading={isLoading} setIsLoading={setIsLoading} email={email} setEmail={setEmail} otpCode={otpCode} setOtpCode={setOtpCode} otpStep={otpStep} setOtpStep={setOtpStep} navigate={navigate} isValidEmail={isValidEmail} />}

            {authMethod !== "register" && <div className="text-center">
              <button type="button" onClick={() => {
              setAuthMethod("register");
              resetForm();
            }} className="text-travel-500 hover:text-travel-600 text-sm">
                {t("auth.noAccount")}
              </button>
            </div>}
          </div>
        </div>

        <div className="hidden md:flex md:w-1/2 relative flex-col items-center justify-center">
          <img src="/lovable-uploads/Checkmytrip-Hero-Image.png" alt="Preview" className="w-[502px] h-[600px] object-cover" />
        </div>
      </div>

      {!isFlutterWebapp && <footer className="w-full py-6 px-4 border-t border-gray-200 bg-white mt-auto">
        <div className="container mx-auto">
          <div className="flex flex-col items-center space-y-4">
            <p className="text-gray-600 font-medium">CheckMyTrip available on</p>
            <div className="flex flex-row gap-4">
              {platform !== "android" && <a href="https://checkmytrip.app.link/get-checkmytrip-ios" id="ios-download" className="inline-flex items-center px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 hover:scale-105 text-sm">
                <i className="fab fa-apple text-lg mr-2" aria-hidden="true"></i>
                <span>App Store</span>
              </a>}
              {platform !== "apple" && <a href="https://checkmytrip.app.link/get-checkmytrip-android" id="play-download" className="inline-flex items-center px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 hover:scale-105 text-sm">
                <i className="fas fa-play text-lg mr-2" aria-hidden="true"></i>
                <span>Google Play</span>
              </a>}
            </div>
            
            <div className="flex flex-row items-center space-x-4 mt-4">
              <img src="/lovable-uploads/61104104-eb0e-4ebe-a350-60449cac5358.png" alt="Travelers" className="h-12 w-auto" />
              <p className="text-gray-700 font-semibold">3M+ travelers use our app!</p>
            </div>
            
            <div className="mt-6 flex justify-center w-full">
              <img src="/lovable-uploads/cfaab934-d21e-4c01-bd8d-cf48c31804e6.png" alt="CheckMyTrip QR Code" className="h-32 w-32 object-contain hidden md:block" />
            </div>
          </div>
        </div>
      </footer>}
    </div>;
};
export default Auth;